import * as React from 'react';
import Styled, { css } from 'styled-components';
import { Transition } from 'react-transition-group';

import { media } from '../../lib/StyledComponents';
import { VALID_TRANSITION_STATES } from '../_ThemeTwo_/Animations/Animations';
import { ESCAPE, ESCAPE_ATL, TAB, VALID_KEYS } from '../_ThemeTwo_/Utils/events/keyCodes';
import { getFocusableElements, REM_NUM_CONVERSION } from '../_ThemeTwo_/Utils';

import { HeaderHeight } from './HeaderContainer';
import { IHeaderLinkProps } from './Common';

const TRANSITION_DURATION: number = 200;

const VALID_KEY_CODES: VALID_KEYS[] = [
  ESCAPE,
  ESCAPE_ATL,
  TAB
];

export interface IHeaderDrawerProps extends IHeaderLinkProps {
  open: boolean;
}

export const HeaderDrawerLink = Styled.li<IHeaderDrawerProps>`
  ${({ breakPoint = 'small', theme, open }) =>
    (css`
      height: 100%;
      display: flex;
      align-content: center;
      align-items: center;
      white-space: nowrap;

      ${media[breakPoint]`
        position: relative;
      `}

      svg {
        fill: currentColor;
      }

      a,
      button {
        cursor: pointer;
        color: ${open ? theme.header.colorSubNavLinkHover : theme.header.colorLink };
        text-decoration: none;
        display: flex;
        align-content: center;
        align-items: center;

        &:hover {
          color: ${theme.header.colorLinkHover};
        }
      }
    `)
  }
`;

HeaderDrawerLink.defaultProps = {
  open: false
};

HeaderDrawerLink.displayName = 'HeaderDrawerLink';

export const HeaderDrawerNavLink = Styled.li<IHeaderLinkProps>`
  ${({ breakPoint = 'small', theme }) =>
    (css`
      margin: 0.875em 0;

      &:last-child {
        border-top: 1px solid ${theme.colorSlate10};
        margin: 0;
        padding: 0.875em 0;
      }

      ${media[breakPoint]`
        border-top: none;
        padding: 0 1em;

        &:first-child {
          border-top: none;
        }

        &:last-child {
          padding: 0.875em 1em;
        }

        &:only-child {
          margin-top: 0;
          padding-top: 0;
          padding-bottom: 0;
          border: none;
        }
      `}
    `)
  }
`;

HeaderDrawerNavLink.displayName = 'HeaderDrawerNavLink';

export const HeaderMobileOnlyDrawerLinkContainer = Styled(HeaderDrawerNavLink)`
  ${({ breakPoint = 'small' }) =>
    (css`
      display: block;
      margin: 0.875em 0;

      ${media[breakPoint]`
        display: none;
      `}
    `)
  }
`;

HeaderMobileOnlyDrawerLinkContainer.displayName = 'HeaderMobileOnlyDrawerLinkContainer';

interface IHeaderDrawerContainerProps extends IHeaderLinkProps {
  transitionState: VALID_TRANSITION_STATES;
  showMenu: boolean;
  headerHeightOffset?: number;
  toggleMenu (): void;
}

export const HeaderDrawerContainer = Styled.ul<IHeaderDrawerContainerProps>`
  ${({ breakPoint = 'small', theme, transitionState, headerHeightOffset }) => {
    const headerOffset = headerHeightOffset && `${headerHeightOffset / REM_NUM_CONVERSION}rem`;

    return css`
      transition: transform ${TRANSITION_DURATION}ms ease-in-out;
      transform-origin: top;
      transform:
        ${(() => {
          switch (transitionState) {
            case VALID_TRANSITION_STATES.entered:
              return 'scaleY(1)';
            case VALID_TRANSITION_STATES.entering:
              return 'scaleY(0)';
            case VALID_TRANSITION_STATES.exiting:
              return 'scaleY(0)';
            default:
              return 'scaleY(1)';
          }
        })()};
      box-shadow: 0 0 5px 0 ${theme.colorSlate10};
      width: 100%;
      position: absolute;
      left: 0;
      border: 1px solid ${theme.colorSlate10};
      border-top: 0;
      background-color: ${theme.colorWhite};
      overflow: hidden;
      z-index: ${theme.themeTwo.global.zIndex.highest};
      line-height: 1.25em;
      top: ${HeaderHeight.Small};

      ${headerHeightOffset && css`
        top: ${headerOffset};
      `}
}
      /* stylelint-disable no-descending-specificity */
      a,
      button {
        padding: 0.75em 1em;
        text-align: center;
        display: block;
        width: 100%;
        text-decoration: none;
        color: ${theme.header.colorLink};

        &:hover {
          color: ${theme.header.colorLinkHover};
        }
      }
      /* stylelint-enable */

      ${media[breakPoint]`
        width: 13.5em;
        left: auto;
        right: 0;
        top: calc(100% + 1px);

        a, button {
          padding: 0.375em 0;
          text-align: left;
          color: ${theme.header.colorHeaderDrawerLinkAboveSmallBP}
        }
      `}
    `;
  }}
`;

HeaderDrawerContainer.displayName = 'HeaderDrawerContainer';

interface IHeaderDrawerComponentProps extends IHeaderDrawerProps {
  id: string;
  headerHeightOffset?: number;
  closeMenu (): void;
}

export const HeaderDrawer: React.FC<IHeaderDrawerComponentProps> = ({ open, children, id, closeMenu, headerHeightOffset }) => {
  const drawerRef = React.useRef<HTMLUListElement | null>(null);
  const [lastFocusableElement, setFocusableEl] = React.useState<HTMLElement | undefined>(undefined);
  const [triggerEl, setTriggerEl] = React.useState<HTMLElement | null>(null);

  const setRef = React.useCallback(
    node => {
      if (node !== null) {
        const focusableEls = getFocusableElements(node);
        setFocusableEl(focusableEls && focusableEls.lastElement);
        drawerRef.current = node;
      }
    },
    []
  );

  // close drawer when click outside
  const handleClickOutside = (e: MouseEvent) => {
    if (!drawerRef || !drawerRef.current) {
      return;
    } else if (!drawerRef.current.contains(e.target as Element)) {
      closeMenu();
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if (!drawerRef || !drawerRef.current || !VALID_KEY_CODES.includes(e.key)) {
      return;
    } else if ((e.key === ESCAPE_ATL || e.key === ESCAPE)) {
      closeMenu();
      if (triggerEl) {
        triggerEl.focus();
      }
    } else if (!e.shiftKey && document.activeElement === (lastFocusableElement && lastFocusableElement)) {
      closeMenu();
    }
  };

  React.useEffect(
    () => {
      if (open) {
        const trigger = document.activeElement as HTMLElement;
        setTriggerEl(trigger);
      }
    },
    [open]
  );

  React.useEffect(() => {
    if (drawerRef.current) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeydown);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    };
  });

  return (
    <Transition
      in={open}
      timeout={{
        appear: 0,
        enter: 0,
        exit: TRANSITION_DURATION,
      }}
    >
      {(state: VALID_TRANSITION_STATES) => (
        state === VALID_TRANSITION_STATES.exited ? null : (
          <HeaderDrawerContainer
            id={id}
            transitionState={state}
            showMenu={open}
            toggleMenu={closeMenu}
            ref={setRef}
            headerHeightOffset={headerHeightOffset}
          >
            {children}
          </HeaderDrawerContainer>
        )
      )}
    </Transition>
  );
};
